import { useState, useEffect } from 'react';
import { DollarSign, Users, Wallet, TrendingUp, RefreshCw, Calendar, Lightbulb, Building2, CircleUser, TrendingUp as TrendingUpIcon } from 'lucide-react';
import { RevenueOverviewCard } from './RevenueOverviewCard';
import { RevenueChart } from './RevenueChart';
import { ClientRankings } from './ClientRankings';
import { InfluencerRankings } from './InfluencerRankings';
import { BrandRankings } from './BrandRankings';
import { UnprofitableCampaigns } from './UnprofitableCampaigns';
import { statisticsService } from '../services/statisticsService';
import type {
  RevenueStatistics,
  ProjectionsStatistics,
  RankedEntity,
  BrandPerformance,
  TimeSeriesDataPoint,
  UnprofitableCampaign,
} from '../types/statistics';
import { useLanguage } from '../contexts/LanguageContext';
import { log } from '../lib/logger';

type TabType = 'clients' | 'influencers' | 'brands';

export function Statistics() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('clients');
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);

  const [revenueStats, setRevenueStats] = useState<RevenueStatistics>({
    client_total: 0,
    influencer_total: 0,
    agency_commission: 0,
    net_margin: 0,
  });

  const [projections, setProjections] = useState<ProjectionsStatistics>({
    potential_client_revenue: 0,
    potential_influencer_costs: 0,
    potential_commission: 0,
    potential_margin: 0,
  });

  const [clientRankings, setClientRankings] = useState<RankedEntity[]>([]);
  const [influencerRankings, setInfluencerRankings] = useState<RankedEntity[]>([]);
  const [brandRankings, setBrandRankings] = useState<BrandPerformance[]>([]);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesDataPoint[]>([]);
  const [unprofitableCampaigns, setUnprofitableCampaigns] = useState<UnprofitableCampaign[]>([]);

  const loadData = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const filters = selectedYear ? { year: selectedYear } : undefined;

      const [revenue, clientsData, influencersData, brandsData, timeData, projectionsData, unprofitable] = await Promise.all([
        statisticsService.getRevenueStatistics(filters),
        statisticsService.getClientRevenueBreakdown(filters),
        statisticsService.getInfluencerRevenueBreakdown(filters),
        statisticsService.getBrandPerformance(filters),
        statisticsService.getTimeSeriesData(filters),
        statisticsService.getProjectionsStatistics(),
        statisticsService.getUnprofitableCampaigns(filters),
      ]);

      setRevenueStats(revenue);
      setClientRankings(clientsData);
      setInfluencerRankings(influencersData);
      setBrandRankings(brandsData);
      setTimeSeriesData(timeData);
      setProjections(projectionsData);
      setUnprofitableCampaigns(unprofitable);
    } catch (error) {
      log.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const currentYear = new Date().getFullYear();
  const yearOptions = [
    { value: undefined, label: 'Todos os Anos' },
    { value: currentYear, label: currentYear.toString() },
    { value: currentYear - 1, label: (currentYear - 1).toString() },
    { value: currentYear - 2, label: (currentYear - 2).toString() },
  ];

  const hasProjections = projections.potential_client_revenue > 0;

  const getTabStyles = (tab: TabType) => {
    if (activeTab === tab) {
      if (tab === 'clients') return 'bg-green-500 text-white border-green-500 shadow-lg shadow-blue-500/50';
      if (tab === 'influencers') return 'bg-purple-500 text-white border-purple-500 shadow-lg shadow-purple-500/50';
      if (tab === 'brands') return 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/50';
    }
    return 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-500 rounded-2xl p-4 shadow-sm">
              <TrendingUpIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Estatísticas</h1>
              <p className="text-gray-500 mt-1">Visão geral de receitas, custos e rankings de performance</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <select
              value={selectedYear || ''}
              onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : undefined)}
              className="bg-white border border-gray-200 text-gray-900 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 text-sm font-medium"
            >
              {yearOptions.map((option) => (
                <option key={option.label} value={option.value || ''}>
                  {option.label}
                </option>
              ))}
            </select>

            <button
              onClick={() => loadData(false)}
              disabled={refreshing}
              className="flex items-center gap-2 bg-accent-600 hover:bg-accent-700 text-white rounded-full px-6 py-3 transition-colors disabled:opacity-50 font-semibold shadow-sm hover:shadow-md"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              Atualizar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <RevenueOverviewCard
            label="RECEITA DE CLIENTES"
            value={revenueStats.client_total}
            icon={DollarSign}
            colorClass="green"
            loading={loading}
          />
          <RevenueOverviewCard
            label="CUSTOS INFLUENCERS"
            value={revenueStats.influencer_total}
            icon={Users}
            colorClass="blue"
            loading={loading}
          />
          <RevenueOverviewCard
            label="COMISSÃO DA AGÊNCIA"
            value={revenueStats.agency_commission}
            icon={Wallet}
            colorClass="purple"
            loading={loading}
          />
          <RevenueOverviewCard
            label="MARGEM LÍQUIDA"
            value={revenueStats.net_margin}
            icon={TrendingUp}
            colorClass="emerald"
            isPercentage
            loading={loading}
          />
        </div>

        {(unprofitableCampaigns.length > 0 || loading) && (
          <UnprofitableCampaigns campaigns={unprofitableCampaigns} loading={loading} />
        )}

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Evolução de Receita & Custos</h2>
          <p className="text-gray-500 mb-6">Análise temporal da receita de clientes vs custos de influencers</p>
          <RevenueChart data={timeSeriesData} loading={loading} />
        </div>

        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setActiveTab('clients')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg border-2 transition-all font-semibold ${getTabStyles('clients')}`}
              >
                <Building2 className="w-5 h-5" />
                <span>Top Clientes</span>
                <span className="bg-white/20 px-2 py-1 rounded-lg text-sm font-bold">
                  {clientRankings.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('influencers')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg border-2 transition-all font-semibold ${getTabStyles('influencers')}`}
              >
                <CircleUser className="w-5 h-5" />
                <span>Top Influencers</span>
                <span className="bg-white/20 px-2 py-1 rounded-lg text-sm font-bold">
                  {influencerRankings.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('brands')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg border-2 transition-all font-semibold ${getTabStyles('brands')}`}
              >
                <TrendingUpIcon className="w-5 h-5" />
                <span>Top Marcas</span>
                <span className="bg-white/20 px-2 py-1 rounded-lg text-sm font-bold">
                  {brandRankings.length}
                </span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'clients' && <ClientRankings clients={clientRankings} loading={loading} />}
            {activeTab === 'influencers' && <InfluencerRankings influencers={influencerRankings} loading={loading} />}
            {activeTab === 'brands' && <BrandRankings brands={brandRankings} loading={loading} />}
          </div>
        </div>

        {hasProjections && (
          <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-amber-500 rounded-xl p-3">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Projeções Futuras</h2>
                <p className="text-gray-600 text-sm">Baseado em campanhas em consultação</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <RevenueOverviewCard
                label="Receita Potencial"
                value={projections.potential_client_revenue}
                icon={DollarSign}
                colorClass="green"
              />
              <RevenueOverviewCard
                label="Custos Estimados"
                value={projections.potential_influencer_costs}
                icon={Users}
                colorClass="blue"
              />
              <RevenueOverviewCard
                label="Comissão Potencial"
                value={projections.potential_commission}
                icon={Wallet}
                colorClass="purple"
              />
              <RevenueOverviewCard
                label="Margem Projetada"
                value={projections.potential_margin}
                icon={TrendingUp}
                colorClass="emerald"
                isPercentage
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
